import { supabase } from '../lib/supabase';

export type PaidStatus = 'pending' | 'succeeded' | 'failed' | 'cancelled' | 'refunded' | 'requires_action';

export type DeliveryStatus = 'pending' | 'preparing' | 'shipped' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'failed' | 'returned';

export interface Purchase {
  id: string;
  offer_id?: string | null;
  transaction_id?: string | null;
  product_id: string;
  buyer_id: string;
  buyer_email?: string | null;
  buyer_name?: string | null;
  seller_id: string;
  seller_name?: string | null;
  purchase_price: number;
  paid_status: PaidStatus;
  delivery_status?: DeliveryStatus;
  tracking_number?: string | null;
  product_name: string;
  product_description?: string | null;
  product_images: string[];
  product_colors: string[];
  product_custom_colors: string[];
  product_sizes: string[];
  product_condition?: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
  product_brand?: string | null;
  product_model?: string | null;
  product_sku?: string | null;
  product_tags: string[];
  created_at: string;
  updated_at: string;
}

export class PurchasesService {
  static async createFromOffer(offerId: string): Promise<Purchase> {
    // If a purchase already exists for this offer, return it (idempotency)
    try {
      const { data: existing, error: existingErr } = await supabase
        .from('purchases')
        .select('*')
        .eq('offer_id', offerId)
        .limit(1);
      if (!existingErr && existing && existing.length > 0) {
        return existing[0] as Purchase;
      }
    } catch {
      // ignore and continue
    }

    // Fetch the offer and product snapshot, then insert purchase
    const { data: offer, error: offerError } = await supabase
      .from('offers')
      .select('*')
      .eq('id', offerId)
      .single();

    if (offerError || !offer) {
      throw new Error(offerError?.message || 'Offer not found');
    }

    const { data: product, error: productError } = await supabase
      .from('products')
      .select('*')
      .eq('id', offer.product_id)
      .single();

    if (productError || !product) {
      throw new Error(productError?.message || 'Product not found');
    }

    const insertPayload = {
      offer_id: offer.id,
      product_id: offer.product_id,
      buyer_id: offer.buyer_id,
      seller_id: offer.seller_id,
      purchase_price: offer.offer_price,
      paid_status: 'pending' as PaidStatus,
      product_name: product.name,
      product_description: product.description ?? null,
      product_images: product.images ?? [],
      product_colors: product.colors ?? [],
      product_custom_colors: product.custom_colors ?? [],
      product_sizes: product.sizes ?? [],
      product_condition: product.condition ?? null,
      product_brand: product.brand ?? null,
      product_model: product.model ?? null,
      product_sku: product.sku ?? null,
      product_tags: product.tags ?? [],
    };

    const { data, error } = await supabase
      .from('purchases')
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      // If it was created concurrently, fetch and return existing
      try {
        const { data: existing, error: existingErr } = await supabase
          .from('purchases')
          .select('*')
          .eq('offer_id', offerId)
          .limit(1);
        if (!existingErr && existing && existing.length > 0) {
          return existing[0] as Purchase;
        }
      } catch {
        // ignore
      }
      throw new Error(error.message || 'Failed to create purchase');
    }
    return data as Purchase;
  }

  static async getMyPurchases(): Promise<Purchase[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase
      .from('purchases')
      .select('*')
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);

    const purchases = (data || []) as Purchase[];

    try {
      const purchaseOfferIds = new Set(
        purchases
          .map(p => p.offer_id)
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
      );

      const { data: acceptedOffers, error: acceptedOffersErr } = await supabase
        .from('offers')
        .select('id')
        .eq('buyer_id', user.id)
        .in('status', ['accepted', 'completed']);

      if (!acceptedOffersErr && Array.isArray(acceptedOffers) && acceptedOffers.length > 0) {
        for (const o of acceptedOffers) {
          const offerId = (o as any)?.id as string | undefined;
          if (!offerId || purchaseOfferIds.has(offerId)) continue;
          try {
            await PurchasesService.createFromOffer(offerId);
          } catch {
            // ignore individual failures
          }
        }

        const { data: refreshed, error: refreshedErr } = await supabase
          .from('purchases')
          .select('*')
          .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
          .order('created_at', { ascending: false });

        if (!refreshedErr) {
          return (refreshed || []) as Purchase[];
        }
      }
    } catch {
      // ignore
    }

    return purchases;
  }

  static async updatePaidStatus(purchaseId: string, status: PaidStatus): Promise<Purchase> {
    const { data, error } = await supabase
      .from('purchases')
      .update({ paid_status: status })
      .eq('id', purchaseId)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as Purchase;
  }
}


